import {fabric} from 'fabric';
import './video';

fabric.UYOImage = fabric.util.createClass(fabric.Image, {
  type: 'uYOImage',
  isError: false,
  toObject(propertiesToInclude) {
    // This is mostly copied from the fabric.Image toObject function
    const {toFixed} = fabric.util;
    const {NUM_FRACTION_DIGITS} = fabric.Object;
    let object = {
      type: this.type,
      variant: this.variant,
      version: fabric.version,
      originX: this.originX,
      originY: this.originY,
      left: toFixed(this.left, NUM_FRACTION_DIGITS),
      top: toFixed(this.top, NUM_FRACTION_DIGITS),
      width: toFixed(this.width, NUM_FRACTION_DIGITS),
      height: toFixed(this.height, NUM_FRACTION_DIGITS),
      fill: this.fill?.toObject ? this.fill.toObject() : this.fill,
      stroke: this.stroke?.toObject ? this.stroke.toObject() : this.stroke,
      strokeWidth: toFixed(this.strokeWidth, NUM_FRACTION_DIGITS),
      strokeDashArray: this.strokeDashArray ? this.strokeDashArray.concat() : this.strokeDashArray,
      strokeLineCap: this.strokeLineCap,
      strokeDashOffset: this.strokeDashOffset,
      strokeLineJoin: this.strokeLineJoin,
      strokeUniform: this.strokeUniform,
      strokeMiterLimit: toFixed(this.strokeMiterLimit, NUM_FRACTION_DIGITS),
      scaleX: toFixed(this.scaleX, NUM_FRACTION_DIGITS),
      scaleY: toFixed(this.scaleY, NUM_FRACTION_DIGITS),
      angle: toFixed(this.angle, NUM_FRACTION_DIGITS),
      flipX: this.flipX,
      flipY: this.flipY,
      opacity: toFixed(this.opacity, NUM_FRACTION_DIGITS),
      shadow: this.shadow?.toObject ? this.shadow.toObject() : this.shadow,
      visible: this.visible,
      backgroundColor: this.backgroundColor,
      fillRule: this.fillRule,
      paintFirst: this.paintFirst,
      globalCompositeOperation: this.globalCompositeOperation,
      skewX: toFixed(this.skewX, NUM_FRACTION_DIGITS),
      skewY: toFixed(this.skewY, NUM_FRACTION_DIGITS),
    };
    if (this.clipPath && !this.clipPath.excludeFromExport) {
      object.clipPath = this.clipPath.toObject(propertiesToInclude);
      object.clipPath.inverted = this.clipPath.inverted;
      object.clipPath.absolutePositioned = this.clipPath.absolutePositioned;
    }
    fabric.util.populateWithProperties(this, object, propertiesToInclude);
    if (!this.includeDefaultValues) {
      object = this._removeDefaultValues(object);
    }
    if (this.srcUrl) {
      object.src = this.srcUrl;
    }
    object.filters = this.filters;
    if (this.srcScale) {
      // Undo the scaling we did to the object to account for the image scaling
      object.width = toFixed(this.width / this.srcScale, NUM_FRACTION_DIGITS);
      object.height = toFixed(this.height / this.srcScale, NUM_FRACTION_DIGITS);
      object.scaleX = toFixed(this.scaleX * this.srcScale, NUM_FRACTION_DIGITS);
      object.scaleY = toFixed(this.scaleY * this.srcScale, NUM_FRACTION_DIGITS);
    }
    return object;
  },
});

/**
 * Scales an image to fit within the specified maximum dimensions while maintaining its aspect ratio.
 * If the image is already within the maximum dimensions, it is returned as is.
 *
 * @param {HTMLImageElement} img - The image element to be scaled.
 * @param {number} [maxWidth=2000] - The maximum allowed width for the scaled image.
 * @param {number} [maxHeight=2000] - The maximum allowed height for the scaled image.
 * @returns {Promise<{image: HTMLImageElement, scale: number}>} - A promise that resolves to an object containing:
 *   - `image`: The scaled image element.
 *   - `scale`: The scaling factor applied to the image.
 */
const scaleImageToMaxDimensions = async (img, maxWidth = 2000, maxHeight = 2000) => {
  if (!img || (img.width <= maxWidth && img.height <= maxHeight)) {
    return {image: img, scale: 1};
  }

  const scale = Math.min(maxWidth / img.width, maxHeight / img.height);
  const targetWidth = Math.round(img.width * scale);
  const targetHeight = Math.round(img.height * scale);

  try {
    // Use createImageBitmap for better performance
    const bitmap = await createImageBitmap(img, {
      resizeWidth: targetWidth,
      resizeHeight: targetHeight,
      resizeQuality: 'high',
    });

    // Create canvas with scaled dimensions
    const c = document.createElement('canvas');
    c.width = targetWidth;
    c.height = targetHeight;

    const ctx = c.getContext('2d', {alpha: true});
    if (!ctx) throw new Error('Failed to get canvas context');

    // Draw the scaled bitmap
    ctx.drawImage(bitmap, 0, 0);

    // Clean up bitmap
    bitmap.close();

    return {
      image: c,
      scale,
    };
  } catch (_error) {
    // Fallback to traditional canvas scaling if createImageBitmap is not supported
    const c = document.createElement('canvas');
    c.width = targetWidth;
    c.height = targetHeight;

    const ctx = c.getContext('2d');
    if (!ctx) throw new Error('Failed to get canvas context');

    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

    return {
      image: c,
      scale,
    };
  }
};

fabric.UYOVideo = fabric.util.createClass(fabric.Video, {
  type: 'uYOImage',
  isError: false,

  initialize(src, options = {}) {
    // Call the parent (fabric.Video) initialize
    this.callSuper('initialize', src, options);

    const videoElement = this.getElement();
    videoElement.preload = 'metadata';

    const updateDimensions = () => {
      if (videoElement.videoWidth && videoElement.videoHeight) {
        this.set({
          width: videoElement.videoWidth,
          height: videoElement.videoHeight,
        });
        if (this.canvas) {
          this.canvas.renderAll();
        }
      }
    };

    // Listen for metadata to update dimensions
    videoElement.addEventListener('loadedmetadata', () => {
      updateDimensions();
    });

    // Log errors if any
    videoElement.addEventListener('error', (err) => {
      evite.error('Video failed to load metadata:', err);
    });

    // Attempt to play once loaded
    setTimeout(() => {
      const playPromise = videoElement.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch((err) => {
          evite.warn('Video play was prevented:', err);
        });
      }
    }, 100);
  },
});

fabric.UYOVideo.fromObject = ({src, ...object}, callback) => {
  const video = new fabric.UYOVideo(src, object);
  callback?.(video, false);
};
// Updated load function to handle both images and videos
const loadMedia = (url, callback, options = {}) => {
  const lowerUrl = url?.toLowerCase() || '';
  const isVideo =
    lowerUrl.endsWith('.mp4') || lowerUrl.endsWith('.webm') || lowerUrl.endsWith('.ogg');

  if (isVideo) {
    // Create a fabric.Video object
    const video = new fabric.UYOVideo(url, options);
    // Assign an id if needed
    if (!video.id) {
      const originalObject = video._originalObject || video.toObject();
      video.id = `${originalObject.left}${originalObject.top}${originalObject.height}${originalObject.width}`;
    }

    callback?.(video);
  } else {
    // Handle image as before
    fabric.util.loadImage(
      url,
      async (img, isError) => {
        const {image, scale} = await scaleImageToMaxDimensions(img);

        const uyoImage = new fabric.UYOImage(image, {
          ...options,
          isError,
          srcUrl: url,
        });
        if (!uyoImage.id) {
          const originalObject = uyoImage._originalObject || uyoImage.toObject();
          uyoImage.id = `${originalObject.left}${originalObject.top}${originalObject.height}${originalObject.width}`;
        }

        if (options?.clipPath?.type) {
          const Klass = fabric.util.getKlass(options.clipPath.type, 'fabric');
          if (Klass) {
            uyoImage.clipPath = new Klass(options.clipPath);
          }
        }
        uyoImage.srcUrl = url;
        uyoImage.srcScale = scale;
        uyoImage._initFilters(options);
        callback(uyoImage);
      },
      null,
      'anonymous'
    );
  }
};

fabric.UYOImage.fromURL = loadMedia;

fabric.UYOImage.fromObject = (_object, callback) => {
  // Need to create a new object
  const object = {..._object};
  fabric.util.loadImage(
    object.src,
    async (img, error) => {
      if (error) {
        callback(null, error);
        return;
      }
      const {image} = await scaleImageToMaxDimensions(img);

      if (object.width && object.width > image?.width) {
        // Scale the object to account for the image scaling we just did.
        const newScale = image.width / object.width;
        object.width = image.width;
        object.height = image.height;
        object.scaleX /= newScale;
        object.scaleY /= newScale;
        object.srcScale = newScale;
      }
      fabric.Image.prototype._initFilters.call(object, object.filters, (filters) => {
        object.filters = filters || [];
        fabric.Image.prototype._initFilters.call(object, [object.resizeFilter], (resizeFilters) => {
          const [firstFilter] = resizeFilters;
          object.resizeFilter = firstFilter;

          const oImg = new fabric.UYOImage(image, object);
          if (!oImg.id) {
            const originalObject = oImg._originalObject || oImg.toObject();
            oImg.id = `${originalObject.left}${originalObject.top}${originalObject.height}${originalObject.width}`;
          }

          if (object?.clipPath?.type) {
            const Klass = fabric.util.getKlass(object.clipPath.type, 'fabric');
            if (Klass) {
              oImg.clipPath = new Klass(object.clipPath);
            }
          }
          oImg._initFilters(object);
          oImg.srcUrl = object.src;
          callback(oImg);
        });
      });
    },
    null,
    'anonymous'
  );
};
